/**
 * JiraCard — one issue row, table-aligned to a shared grid template.
 *
 * Columns: KEY · SUMMARY · ASSIGNEE · DEV START · DEV END · STATUS · % · HEALTH · ↗
 *
 * Schedule columns track the team's "Dev Start" / "Dev End" custom
 * fields — distinct from Jira's built-in Start / Due dates (which can
 * include planning + review). Dev dates answer "when does development
 * work begin / end on this card."
 *
 * The grid template is exported as `JIRA_ROW_GRID` so the parent panel
 * can render a matching header row above the list — that's what makes
 * each pill / value land under a labeled column instead of free-floating
 * after the summary.
 */
import { AlertTriangle, ExternalLink, Link2Off, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import type { JiraIssue, JiraParentType } from "@/hooks/useJira";
import { IssueTypeChip, PriorityChip } from "./JiraIssueChips";

/** Shared grid template for the header row + every issue row. The
 *  parent column widths match exactly so columns align. Tailwind
 *  arbitrary value: every fragment is one column. */
// Column order: Key | Summary | Type | Pri | Assignee | Start | Due | Status | % | Health | ↗ | unlink
// Brett's preferred reading sequence — identifier first, what-it-is
// second, classifying chips after.
export const JIRA_ROW_GRID =
  "grid grid-cols-[88px_minmax(0,1fr)_28px_28px_140px_82px_82px_120px_56px_72px_24px_24px] items-center gap-3";

interface Props {
  issue: JiraIssue;
  /** Current parent (so the "Also linked to" chip filters self out). */
  parentType?: JiraParentType;
  parentId?: string;
  onOpen: (issueKey: string) => void;
  /** Remove this issue's link to the current parent. Omitted when the
   *  surface can't unlink (e.g. Synnergie Workload). Only offered on
   *  top-level rows: children are rolled up from Jira's hierarchy, not
   *  planner links, so there is nothing to unlink on them. */
  onUnlink?: (issueKey: string) => void;
  unlinking?: boolean;
}

function statusPillClass(category: string | null | undefined): string {
  // Sitewide rule: gray (not in progress) / sky (in progress) / emerald (done).
  switch (category) {
    case "done":
      return "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30";
    case "indeterminate":
      return "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-500/30";
    case "new":
    default:
      return "bg-raised text-ink-3 ring-1 ring-line";
  }
}

function percentPillClass(pct: number): string {
  // Same gray/sky/emerald rule as the status pill: any non-zero
  // partial progress reads as 'in progress' (sky), 100 as 'done'
  // (emerald), 0 as 'not started' (slate).
  if (pct >= 100) return "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30";
  if (pct > 0) return "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-500/30";
  return "bg-raised text-ink-muted ring-1 ring-line";
}

/** Parse either a YYYY-MM-DD date or a full ISO datetime — Jira's
 *  built-in duedate is the former; team custom-field datetimes (e.g.
 *  Dev Start / Dev End) often come back as the latter. Without this
 *  split, appending "T00:00:00" to an already-timestamped value
 *  produces invalid input and the cell renders em-dash. */
function parseDateOrDatetime(iso: string): Date {
  // YYYY-MM-DD exactly = 10 chars and no "T" → date-only. Append a
  // midnight stamp so the constructor doesn't slip to UTC.
  if (iso.length === 10 && !iso.includes("T")) {
    return new Date(iso + "T00:00:00");
  }
  return new Date(iso);
}

/** Compact short date "Mon D" or null when unparseable. */
function fmtShort(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = parseDateOrDatetime(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtFull(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = parseDateOrDatetime(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Date cell — short label, em-dash when null, rose tint when overdue
 *  (i.e. due date in the past on a non-Done issue). */
function DateCell({
  iso,
  isDue,
  done,
}: {
  iso: string | null | undefined;
  isDue: boolean;
  done: boolean;
}) {
  const short = fmtShort(iso);
  if (!short) return <span className="text-xs text-ink-faint">—</span>;

  let overdue = false;
  if (isDue && !done && iso) {
    const t = parseDateOrDatetime(iso).getTime();
    if (!Number.isNaN(t)) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      overdue = t < today.getTime();
    }
  }

  return (
    <span
      className={cn(
        "tabular-nums text-xs",
        overdue ? "font-semibold text-rose-600 dark:text-rose-400" : "text-ink-3",
      )}
      title={fmtFull(iso) ?? undefined}
    >
      {short}
    </span>
  );
}

const HEALTH_TONE: Record<string, { dot: string; text: string; label: string }> = {
  green:  { dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300", label: "Green" },
  yellow: { dot: "bg-amber-400",   text: "text-amber-700 dark:text-amber-300",   label: "Yellow" },
  red:    { dot: "bg-rose-500",    text: "text-rose-700 dark:text-rose-300",    label: "Red" },
};

function HealthCell({ value }: { value: string | null | undefined }) {
  const v = (value || "").toLowerCase();
  const meta = HEALTH_TONE[v];
  if (!meta) return <span className="text-xs text-ink-faint">—</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs" title={`Project Health: ${meta.label}`}>
      <span className={cn("inline-block h-2 w-2 rounded-full", meta.dot)} />
      <span className={cn("font-medium", meta.text)}>{meta.label}</span>
    </span>
  );
}

/**
 * Header row used above the list. Same grid template; uppercase tracked
 * labels in slate-500. Renders `null` columns where the data column is
 * just visual chrome (the open-in-Jira ↗ icon).
 */
export function JiraTableHeader() {
  return (
    <div
      className={cn(
        JIRA_ROW_GRID,
        "border-b border-line px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted",
      )}
    >
      <span>Key</span>
      <span>Summary</span>
      <span title="Issue type" className="text-center">Type</span>
      <span title="Priority" className="text-center">Pri</span>
      <span>Assignee</span>
      <span title="Team's Dev Start custom field — when development is scheduled to begin.">Dev Start</span>
      <span title="Team's Dev End custom field — when development is scheduled to finish.">Dev End</span>
      <span>Status</span>
      <span className="text-right">%</span>
      <span>Health</span>
      <span aria-hidden />
      <span aria-hidden />
    </div>
  );
}

export function JiraCard({ issue, parentType, parentId, onOpen, onUnlink, unlinking }: Props) {
  // "Also linked to" — every OTHER planner item this issue is pinned to.
  // We filter out the current parent so the user only sees other
  // attachments (a card pinned only here renders no chip). We ALSO
  // filter out the rolled-up source (via_project_id) because the
  // "via" chip already surfaces that relationship — duplicating it as
  // "also" reads like a stutter, especially when the Jira key, the
  // source-project ID, and the linked-to ID all happen to match
  // (a common convention — planner-project named after its Jira Epic).
  const alsoLinkedTo = (issue.linked_to ?? []).filter((l) => {
    if (parentType && parentId && l.scope_type === parentType && l.id === parentId) return false;
    if (issue.via_project_id && l.scope_type === "project" && l.id === issue.via_project_id) return false;
    return true;
  });
  const children = issue.children ?? [];
  const childDoneCount = children.filter((c) => c.status_category === "done").length;

  const childEffective = (c: JiraIssue): number => {
    if (typeof c.pct_complete === "number") return c.pct_complete;
    return c.status_category === "done" ? 1 : 0;
  };
  const parentPct =
    typeof issue.pct_complete === "number"
      ? Math.round(issue.pct_complete * 100)
      : children.length > 0
        ? Math.round(
            (children.reduce((s, c) => s + childEffective(c), 0) / children.length) * 100,
          )
        : issue.status_category === "done"
          ? 100
          : 0;
  const parentDone = issue.status_category === "done";

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      <button
        onClick={() => onOpen(issue.issue_key)}
        className={cn(JIRA_ROW_GRID, "w-full px-3 py-2 text-left transition-colors hover:bg-raised")}
      >
        {/* KEY */}
        <span className="flex items-center gap-1.5 font-mono text-xs text-ink-muted">
          {issue.last_sync_status === "error" && (
            <AlertTriangle
              className="h-3.5 w-3.5 shrink-0 text-rose-500"
              aria-label="Last sync failed"
            />
          )}
          {issue.issue_key}
        </span>

        {/* SUMMARY (with child-count + also-linked-to badges inline) */}
        <span className="flex min-w-0 items-center gap-2 text-sm text-ink">
          <span className="truncate">{issue.summary ?? "(no summary)"}</span>
          {children.length > 0 && (
            <span className="shrink-0 rounded-full bg-raised px-1.5 py-0.5 font-mono text-[10px] text-ink-3 ring-1 ring-line">
              {childDoneCount}/{children.length}
            </span>
          )}
          {alsoLinkedTo.length > 0 && (
            <span
              className="shrink-0 rounded-full bg-amber-50 dark:bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30"
              title={`Also linked to: ${alsoLinkedTo.map((l) => `${l.id} (${l.scope_type})`).join(", ")}`}
            >
              also: {alsoLinkedTo.map((l) => l.id).join(", ")}
            </span>
          )}
        </span>

        {/* TYPE */}
        <span className="flex items-center justify-center">
          <IssueTypeChip type={issue.issue_type} />
        </span>

        {/* PRIORITY */}
        <span className="flex items-center justify-center">
          <PriorityChip priority={issue.priority} />
        </span>

        {/* ASSIGNEE */}
        <span className="truncate text-xs text-ink-muted" title={issue.assignee ?? ""}>
          {issue.assignee ?? <span className="text-ink-faint">—</span>}
        </span>

        {/* DEV START — team's custom field; isDue=false so it's not
            painted rose when stale. */}
        <DateCell iso={issue.dev_start_date ?? null} isDue={false} done={parentDone} />

        {/* DEV END — treated as the "is it past?" date so an
            in-progress card whose dev_end is in the past renders rose. */}
        <DateCell iso={issue.dev_end_date ?? null} isDue done={parentDone} />

        {/* STATUS */}
        <span
          className={cn(
            "truncate rounded-full px-2 py-0.5 text-center text-[11px] font-medium",
            statusPillClass(issue.status_category),
          )}
          title={issue.status ?? ""}
        >
          {issue.status ?? "—"}
        </span>

        {/* % */}
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-center text-[11px] font-medium tabular-nums",
            percentPillClass(parentPct),
          )}
          title={
            typeof issue.pct_complete === "number"
              ? "Synced from Jira"
              : children.length > 0
                ? `${childDoneCount} of ${children.length} sub-tasks done`
                : "Computed from this issue's status"
          }
        >
          {parentPct}%
        </span>

        {/* HEALTH */}
        <HealthCell value={issue.health} />

        {/* OPEN IN JIRA */}
        {issue.url ? (
          <a
            href={issue.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-ink-faint hover:text-brand-navy-600 dark:hover:text-brand-navy-400"
            title="Open in Jira"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span aria-hidden />
        )}

        {/* UNLINK — removes the planner link, never the Jira issue.
            role="button" rather than a real <button> because this row
            is itself a button; nesting one would be invalid HTML (same
            reason the ↗ above is an <a> with stopPropagation).
            Rolled-up cards (via_project_id) aren't linked to THIS
            parent, so there's nothing to unlink on them. */}
        {onUnlink && !issue.via_project_id ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onUnlink(issue.issue_key);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onUnlink(issue.issue_key);
              }
            }}
            className="cursor-pointer text-ink-faint hover:text-rose-600 dark:hover:text-rose-400"
            title={`Unlink ${issue.issue_key} from this ${parentType ?? "project"}`}
            aria-label={`Unlink ${issue.issue_key}`}
          >
            {unlinking ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Link2Off className="h-3.5 w-3.5" />
            )}
          </span>
        ) : (
          <span aria-hidden />
        )}
      </button>

      {children.length > 0 && (
        <ul className="border-t border-line-soft bg-canvas/60">
          {children.map((child) => {
            const childPct =
              typeof child.pct_complete === "number"
                ? Math.round(child.pct_complete * 100)
                : child.status_category === "done"
                  ? 100
                  : 0;
            const childDone = child.status_category === "done";
            return (
              <li key={child.issue_key}>
                <button
                  onClick={() => onOpen(child.issue_key)}
                  className={cn(
                    JIRA_ROW_GRID,
                    "w-full px-3 py-1.5 text-left transition-colors hover:bg-raised",
                  )}
                >
                  {/* KEY (indented to show hierarchy) */}
                  <span className="flex items-center gap-1.5 pl-4 font-mono text-[11px] text-ink-muted">
                    {child.last_sync_status === "error" && (
                      <AlertTriangle className="h-3 w-3 shrink-0 text-rose-500" />
                    )}
                    {child.issue_key}
                  </span>

                  {/* SUMMARY */}
                  <span className="truncate text-xs text-ink-2">
                    {child.summary ?? "(no summary)"}
                  </span>

                  {/* TYPE */}
                  <span className="flex items-center justify-center">
                    <IssueTypeChip type={child.issue_type} size="xs" />
                  </span>

                  {/* PRIORITY */}
                  <span className="flex items-center justify-center">
                    <PriorityChip priority={child.priority} size="xs" />
                  </span>

                  {/* ASSIGNEE */}
                  <span className="truncate text-xs text-ink-subtle" title={child.assignee ?? ""}>
                    {child.assignee ?? <span className="text-ink-faint">—</span>}
                  </span>

                  {/* DEV START */}
                  <DateCell iso={child.dev_start_date ?? null} isDue={false} done={childDone} />

                  {/* DEV END */}
                  <DateCell iso={child.dev_end_date ?? null} isDue done={childDone} />

                  {/* STATUS */}
                  <span
                    className={cn(
                      "truncate rounded-full px-2 py-0.5 text-center text-[11px] font-medium",
                      statusPillClass(child.status_category),
                    )}
                    title={child.status ?? ""}
                  >
                    {child.status ?? "—"}
                  </span>

                  {/* % */}
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-center text-[11px] font-medium tabular-nums",
                      percentPillClass(childPct),
                    )}
                  >
                    {childPct}%
                  </span>

                  {/* HEALTH */}
                  <HealthCell value={child.health} />

                  {/* OPEN IN JIRA */}
                  {child.url ? (
                    <a
                      href={child.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-ink-faint hover:text-brand-navy-600 dark:hover:text-brand-navy-400"
                      title="Open in Jira"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span aria-hidden />
                  )}

                  {/* No unlink on children — they're Jira hierarchy,
                      not planner links. Empty cell keeps alignment. */}
                  <span aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
